import Loading from 'components/Basic/Loading';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import ContentHeader from '../ContentHeader';
import { useEffect, useRef, useState } from 'react';
import AuditLogsTable from 'components/Dashboard/AuditLogs/AuditLogsTable';
import { useLazyGetAuditLogsListQuery } from 'core/auditLogs/AuditLogsService';
import { setAuditLogsData } from 'core/auditLogs/AuditLogsSlice';
import AuditLogsFilter from 'components/Dashboard/AuditLogs/AuditLogsFilter';

import toast from 'components/Basic/Toast';

const AuditLogsList = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const { list } = useAppSelector(state => state.auditLogsReducer);

  const [limit, setLimit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [searchFilterQuery, setSearchFilterQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState('createdAt desc');
  const [searchText, setSearchText] = useState('');

  const [getAuditLogsList] = useLazyGetAuditLogsListQuery();

  const fetchData = async params => {
    setIsLoading(true);
    try {
      getAuditLogsList(params)
        .unwrap()
        .then(res => {
          if (res.data.length === 0) {
            toast.error({
              title: intl.formatMessage({ id: 'auditLogs not found' }),
            });
            dispatch(setAuditLogsData({ list: { data: [], pagination: { total: 0 } } }));
          } else {
            dispatch(setAuditLogsData({ list: res }));
          }
        })
        .catch(e => {
          console.log(e);
          toast.error({
            title: intl.formatMessage({ id: 'auditLogs fecth error' }),
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (e) {
      console.log(e);
      toast.error({
        title: intl.formatMessage({ id: 'auditLogs fecth error' }),
      });
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const handleChangePage = (newOffset, newLimit, newFilterQuery, newSort) => {
    setOffset(newOffset);
    setLimit(newLimit);

    fetchData({
      offset: newOffset,
      limit: newLimit,
      filter: newFilterQuery || filterQuery + searchFilterQuery,
      sort: newSort || sort,
    });
  };

  const getInitialData = () => {
    fetchData({
      offset: 0,
      limit: 10,
      filter: '',
      sort: 'createdAt desc',
    });
  };

  useEffect(() => {
    isMounted.current = true;
    getInitialData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSort = value => {
    setSort(value);
  };

  const handleSearch = e => {
    setSearchText(e.target.value);
  };

  const handleKeyDownSearch = e => {
    const { key, target } = e;
    const isEnter = key === 'Enter';
    const isBackspaceClear = key === 'Backspace' && target.value.length === 1;

    if (!isEnter && !isBackspaceClear) return;

    let newSearchFilterQuery = '';
    let newFilter = filterQuery;
    let newOffset = 0;

    if (isEnter && searchText) {
      if (!isNaN(searchText)) {
        newSearchFilterQuery += `&entityId=${searchText}`;
      } else {
        newSearchFilterQuery = `&description=${searchText}`;
        newSearchFilterQuery += `&username=${searchText}`;
      }
      newFilter = filterQuery + newSearchFilterQuery;
    }

    if (isBackspaceClear || (isEnter && !searchText)) {
      newSearchFilterQuery = '';
      newFilter = filterQuery;
    }

    setSearchFilterQuery(newSearchFilterQuery);
    setOffset(newOffset);

    fetchData({
      limit,
      sort: 'id asc',
      filter: newFilter,
      offset: newOffset,
    });
  };

  const handleApplyFilter = () => {
    const hasFilter = filterQuery !== '';
    const hasSearch = searchFilterQuery !== '';

    const baseParams = {
      limit,
      sort: 'id asc',
      offset: hasFilter || hasSearch ? 0 : offset,
    };

    let filter = '';
    if (hasFilter && !hasSearch) filter = filterQuery;
    if (hasSearch && !hasFilter) filter = searchFilterQuery;
    if (hasFilter && hasSearch) filter = filterQuery + searchFilterQuery;

    fetchData({
      ...baseParams,
      ...(filter && { filter }),
    });
  };

  const handleActionStateFilter = value => {
    if (value === 'all') {
      setFilterQuery('');
    } else {
      setFilterQuery(`&action=${value}`);
    }
  };

  return (
    <div className="auditLogs_list_layout">
      <Loading visible={isLoading} />
      <ContentHeader type="audit_logs" />
      <div className="auditLogs_filter_wrapper">
        <AuditLogsFilter
          searchText={searchText}
          onActionStateFilter={handleActionStateFilter}
          onSort={handleSort}
          onSearch={handleSearch}
          onKeyDownSearch={handleKeyDownSearch}
          onApplyFilter={handleApplyFilter}
        />
        <AuditLogsTable
          auditLogsData={list?.data || []}
          total={list?.pagination?.total || 0}
          onChangePage={handleChangePage}
        />
      </div>
    </div>
  );
};

export default AuditLogsList;
